import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { useTelegram } from '../hooks/useTelegram';
import { speechApi } from '../lib/api';

interface SpeechToTextProps {
  onResult: (text: string) => void;
  disabled?: boolean;
}

// Web Speech API (для браузеров с поддержкой)
const SpeechRecognition = 
  (window as any).SpeechRecognition || 
  (window as any).webkitSpeechRecognition;

// MediaRecorder API (для записи аудио)
const isMediaRecorderSupported = typeof MediaRecorder !== 'undefined';

export function SpeechToText({ onResult, disabled }: SpeechToTextProps) {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interimText, setInterimText] = useState('');
  
  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const { hapticFeedback, showAlert } = useTelegram();

  // Проверяем поддержку (Web Speech API или MediaRecorder)
  const useWebSpeech = !!SpeechRecognition;
  const isSupported = useWebSpeech || isMediaRecorderSupported;

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Запись через MediaRecorder (для Telegram WebView)
  const startRecording = useCallback(async () => {
    if (!isMediaRecorderSupported || disabled) return;

    setError(null);
    hapticFeedback('medium');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        
        // Останавливаем поток
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;

        if (audioBlob.size === 0) {
          setError('Запись пуста');
          setIsListening(false);
          hapticFeedback('error');
          return;
        }

        // Отправляем на сервер для распознавания
        setIsProcessing(true);
        try {
          const result = await speechApi.transcribe(audioBlob);
          if (result.text && result.text.trim()) {
            onResult(result.text);
            hapticFeedback('success');
          } else {
            setError('Текст не распознан');
            hapticFeedback('error');
          }
        } catch (err: any) {
          console.error('Transcription error:', err);
          let errorMsg = 'Ошибка распознавания';
          
          if (err.status === 503) {
            errorMsg = 'Сервис распознавания не настроен';
          } else if (err.status === 400) {
            errorMsg = 'Неверный формат аудио';
          } else if (err.status === 403) {
            errorMsg = 'Доступ запрещен';
          } else if (err.status === 500) {
            errorMsg = 'Ошибка сервера';
          } else if (err.message) {
            errorMsg = err.message;
          }
          
          setError(errorMsg);
          showAlert(errorMsg);
          hapticFeedback('error');
        } finally {
          setIsProcessing(false);
          setIsListening(false);
        }
      };

      recorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event);
        setError('Ошибка записи аудио');
        setIsListening(false);
        hapticFeedback('error');
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      };

      recorder.start();
      setIsListening(true);
    } catch (err: any) {
      console.error('MediaRecorder start error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Разрешите доступ к микрофону в настройках');
        showAlert('Разрешите доступ к микрофону в настройках браузера');
      } else if (err.name === 'NotFoundError') {
        setError('Микрофон не найден');
      } else if (err.name === 'NotSupportedError') {
        setError('Запись аудио не поддерживается');
      } else {
        setError('Не удалось начать запись. Проверьте доступ к микрофону');
      }
      hapticFeedback('error');
    }
  }, [disabled, hapticFeedback, onResult, showAlert]);

  // Распознавание через Web Speech API (для обычных браузеров)
  const startWebSpeech = useCallback(() => {
    if (!SpeechRecognition || disabled) return;

    setError(null);
    hapticFeedback('medium');

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'ru-RU';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setInterimText('');
      };

      recognition.onresult = (event: any) => {
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }

        setInterimText(interim);

        if (final) {
          onResult(final);
          hapticFeedback('success');
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          setError('Разрешите доступ к микрофону');
        } else if (event.error === 'no-speech') {
          setError('Речь не обнаружена');
        } else if (event.error === 'network') {
          setError('Ошибка сети');
        } else {
          setError('Диктовка недоступна');
        }
        
        hapticFeedback('error');
      };

      recognition.onend = () => {
        setIsListening(false);
        setInterimText('');
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Speech recognition start error:', err);
      setError('Диктовка недоступна');
      hapticFeedback('error');
    }
  }, [disabled, hapticFeedback, onResult]);

  const startListening = useCallback(() => {
    if (useWebSpeech) {
      startWebSpeech();
    } else {
      startRecording();
    }
  }, [useWebSpeech, startWebSpeech, startRecording]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsListening(false);
    hapticFeedback('light');
  }, [hapticFeedback]);

  // Не поддерживается
  if (!isSupported) {
    return (
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled
          className="opacity-50"
          title="Диктовка не поддерживается в этом браузере"
        >
          <Mic className="w-4 h-4 mr-1" />
          <span className="text-xs">Недоступно</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        {isListening && interimText && (
          <span className="text-xs text-gray-400 italic max-w-[150px] truncate">
            {interimText}...
          </span>
        )}
        
        <Button
          type="button"
          variant={isListening ? 'danger' : 'secondary'}
          size="sm"
          onClick={isListening ? stopListening : startListening}
          disabled={disabled || isProcessing}
          className={isListening ? 'animate-pulse' : ''}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              <span>Обработка...</span>
            </>
          ) : isListening ? (
            <>
              <Square className="w-4 h-4 mr-1" />
              <span>Стоп</span>
            </>
          ) : (
            <>
              <Mic className="w-4 h-4 mr-1" />
              <span>Диктовать</span>
            </>
          )}
        </Button>
      </div>
      
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="w-3 h-3" />
          <span>{error}</span>
        </div>
      )}
      
      {isListening && !isProcessing && (
        <span className="text-xs text-green-500 flex items-center gap-1">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          {useWebSpeech ? 'Слушаю...' : 'Записываю...'}
        </span>
      )}
    </div>
  );
}
